<script setup>
import { ref, computed } from 'vue';
import { useCommonStore } from '@/stores/common';
import { useReCaptcha } from 'vue-recaptcha-v3';
import axios from 'axios';

const store = useCommonStore();
const { executeRecaptcha, recaptchaLoaded } = useReCaptcha();

const contactName = ref(null);
const contactEmail = ref(null);
const contactMessage = ref(null);
const iconsURL = ref(store.cloudfrontURL+'icons/');
const imgURL = ref(store.cloudfrontImgURL);
const contactFormURL = ref(store.contactUsURL());
const reCaptchaURL = ref(store.reCaptchaURL());
const contactFormMessages = ref('');
const showContactFormMessages = ref(false);
const contactUsFormBackground = computed(() => `url('${imgURL.value}contact-us-background.png')`);

async function submitMessage() {
    showContactFormMessages.value = false;
    contactFormMessages.value = '';
    await recaptchaLoaded();
    const token = await executeRecaptcha('sendmessage');
    const payload = {
        'token':token
    }
    axios.post(reCaptchaURL.value,payload)
        .then(res => {
            //console.log(res);
            if(res.data.success && res.data.isVerified) {
                sendMessage();
            } else {
                console.log('reCaptcha Error.');
                contactFormMessages.value = 'Error sending message, please try again.';
                showContactFormMessages.value = true;
            }
        })
        .catch(err => {
            console.log('reCaptcha Error');
            console.log(err);
            contactFormMessages.value = 'Error sending message, please try again.';
            showContactFormMessages.value = true;
        }); 
}

function sendMessage() {
    if(
        contactName.value !== null &&
        contactEmail.value !== null &&
        contactMessage.value !== null
    ) {
        const payload = {
            'name':contactName.value,
            'email':contactEmail.value,
            'message':contactMessage.value
        }

        axios.post(contactFormURL.value,payload)
            .then(res => {
                // console.log(res);
                if(res.data.success) {
                    contactFormMessages.value = 'Thank you for your message. Someone will be in touch.';
                    showContactFormMessages.value = true;
                    contactName.value = null;
                    contactEmail.value = null;
                    contactMessage.value = null;
                } else {
                    contactFormMessages.value = 'Error sending message, please try again.';
                    showContactFormMessages.value = true;
                }
            })
            .catch(err => {
                console.log('Send Error');
                console.log(err);
                contactFormMessages.value = 'Error sending message, please try again.';
                showContactFormMessages.value = true;
            });
    } else {
        contactFormMessages.value = 'Please complete the form before submitting.';
        showContactFormMessages.value = true;
    }
}
</script>

<template>
    <div id="contact-us-form">
        <div id="contact-us-form-content" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div class="contact-us-form-address">
                <h3 class="clarity-h3-white">GET IN TOUCH</h3>
                <p class="clarity-body-text-white clarity-margin-bottom-s">We have UK offices in London, Woking and Cambridge. Get in touch using the form or any of the details below, we’d love to hear from you.</p>
                <div class="flex items-center mb-[1rem]">
                    <img :src="iconsURL+'phone-icon.png'" alt="Phone Icon">
                    <p class="clarity-body-text-white ml-[5px]">+44 800 368 7511</p>
                </div>
                <div class="flex items-center mb-[1rem]">
                    <img :src="iconsURL+'email-icon.png'" alt="Email Icon">
                    <p class="clarity-body-text-white email ml-[5px]">enquiries@clarityglobal.com</p>
                </div>
                <div class="flex">
                    <img class="mt-[5px]" :src="iconsURL+'address-icon.png'" alt="Address Icon">
                    <div class="ml-[5px]">
                        <p class="clarity-body-text-white address text-left">1 Crown Square, Woking, Surrey </p>
                        <p class="clarity-body-text-white postcode text-left">GU21 6HR</p>
                    </div>
                </div>  
            </div>
            <div class="lg:col-span-2">
                <input id="contact-name" name="contact-name" class="w-full mb-[1rem]" v-model="contactName" type="text" placeholder="Name*...">
                <input id="contact-email" name="contact-email" class="w-full mb-[1rem]" v-model="contactEmail" type="email" placeholder="Email Address*...">
                <textarea id="contact-message" name="contact-message" class="w-full mb-[1rem]" v-model="contactMessage" placeholder="Message..."></textarea>
                <div class="flex justify-between flex-wrap">
                    <div class="recaptcha-terms flex items-center">
                        <p>This site is protected by reCAPTCHA and the<br>Google <a href="https://policies.google.com/privacy" target="_blank">Privacy Policy</a> and
                        <a href="https://policies.google.com/terms" target="_blank">Terms of Service</a> apply.</p>
                    </div>
                    <div class="flex items-center">
                        <button @click="submitMessage" class="clarity-button-orange send-button">Send Message</button>
                    </div>
                </div>
                <div v-if="showContactFormMessages" id="contact-form-messages">
                    <p class="clarity-small-grey-text">{{ contactFormMessages }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="css" scoped>
    #contact-us-form {
        min-height: 612px;
        background-color: var(--clarity-blue);
        background-image: v-bind(contactUsFormBackground);
        background-repeat: no-repeat;
        background-size: cover;
    }
    #contact-us-form {
        margin-right: 0px !important;
    }
    #contact-us-form-content {
        padding-top: 120px;
        padding-bottom: 100px;
        padding-left: calc(15%);
        padding-right: calc(15%);
    }
    .recaptcha-terms {
        background-color: var(--clarity-pure-white);
        padding: 10px 20px 10px 20px;
        border-radius: 10px;
        border: 1px solid var(--clarity-light-grey);
        margin-bottom: 20px;
    }
    .recaptcha-terms p {
        font-size: 14px;
        margin: 0px;
        color: var(--clarity-grey);
    }
    .recaptcha-terms a {
        color: var(--clarity-light-blue);
    }
    input {
        height: 54px;
        padding: 15px 20px;
        font-size: 18px;
        font-family: var(--clarity-font-family-arial);
        background: var(--clarity-pure-white) 0% 0% no-repeat padding-box;
        border: 1px solid var(--clarity-light-grey);
        border-radius: 27px;
        opacity: 1;
    }
    textarea {
        height: 150px;
        padding: 15px 20px;
        font-size: 18px;
        font-family: var(--clarity-font-family-arial);
        background: var(--clarity-pure-white) 0% 0% no-repeat padding-box;
        border: 1px solid var(--clarity-light-grey);
        border-radius: 27px;
        opacity: 1;
    }
    .send-button {
        width: 210px;
        height: 54px;
        margin-bottom: 20px;
    }
    #contact-us-form-content p img {
        vertical-align: middle;
        padding-right: 10px;
    }
    .address {
        margin-bottom: 0px;
    }
    .postcode {
        margin: 0;
    }
    #contact-form-messages {
        margin-top: 30px;
        background-color: var(--clarity-pure-white);
        padding: 5px 10px;
        border-radius: 10px;
        border: 1px solid var(--clarity-light-grey);
        text-align: center;
    }
    .contact-us-form-address img {
        width: 24px;
        height: 24px;
    }
    /* Default Big Screen 1920px */
    /* Mid Screen */
    @media only screen and (max-width: 1366px) {}
    /* Small Screen */
    @media only screen and (max-width: 1024px) {}
    /* Tablet */
    @media only screen and (max-width: 767px) {
        .contact-us-form-address {
        width: 100%;
    }
    }
    /* Mobile */
    @media only screen and (max-width: 375px) {
        .email {
            font-size: 12px;
        }
        .send-button {
            width: 100%;
            height: auto;
            padding: 20px;
        }
    }
</style>